import { APIScope, LocationScope } from '@swivl-mono/types';
import { Suspense, useEffect } from 'react';
import { Navigate, Outlet, Route, Routes, useNavigate } from 'react-router-dom';
import { RouteShowIf } from '../../Components/ShowIf/RouteShowIf';
import { NavigationHelper } from '../../Helpers/Navigation/Navigation.Helper';
import { AdminView } from '../../Views/Admin/Admin.View';
import { OnboardingView } from '../../Views/Admin/Onboarding/Onboarding.View';
import { ServerDebugView } from '../../Views/Admin/ServerDebug/Admin.ServerDebug.View';
import DashboardView from '../../Views/Dashboard/DashboardView';
import { InviteView } from '../../Views/Login/Invites/InviteView';
import { LoginLayout } from '../../Views/Login/Layout/Login.Layout';
import { PropertiesView } from '../../Views/Properties/Properties.View';
import { AccountSettingsView } from '../../Views/Settings/Account/Account.Settings.View';
import { MembersSettingsView } from '../../Views/Settings/Members/Members.Settings.View';
import { RolesSettingsView } from '../../Views/Settings/Roles/Roles.Settings.View';
import { SettingsView } from '../../Views/Settings/Settings.View';

// import { LocationSubpageInfo_Location } from '../../Worksheets/Details/Pages/Location.Subpage.Info-Location';
import { WorksheetLayoutView } from '../../Components/Worksheets/Layout/Worksheet.Layout.View';
import { AdminVariablesEditor } from '../../Views/Admin/Variables/Admin.VariablesEditor';
import { CortexWorksheetsData } from '../../Worksheets/Cortex/Cortex.Worksheets.Data';
import { LocationWorksheetsData } from '../../Worksheets/Details/Location.Worksheets.Data';
import { PageLoadingView } from '../Layout/Loading/LoadingView';

import { BrandDetailsView } from '../../Views/Properties/Brands/Brand.Details.View';
import BrandsView from '../../Views/Properties/Brands/Brands.View';
import { EmbedDetailsView } from '../../Views/Properties/Embeds/Embed.Details.View';
import EmbedsView from '../../Views/Properties/Embeds/Embeds.View';

import React from 'react';
import EmbedTester from '../../Views/Admin/EmbedTester/EmbedTester';
import AgentSettingsView from '../../Views/Conversations/Components/Agent/AgentSettingsView';
import ConversationsView from '../../Views/Conversations/ConversationsView';
import EditorWrapper from '../../Views/Quests/EditorWrapper';
import ReviewsView from '../../Views/Reviews/Reviews.View';
import AIAssistSettingsView from '../../Views/Settings/AIAssistSettings/AIAssistSettings.View';
import AnalyticsSettingsView from '../../Views/Settings/Analytics/Analytics.Settings.View';
import AppSettingsView from '../../Views/Settings/Apps/AppsView';
import BrainsSettingView from '../../Views/Settings/Brains/BrainsSettingView';
import EscalationsView from '../../Views/Settings/Escalations/EscalationsView';
import FMSDetailsView from '../../Views/Settings/FMSIntegrations/FMSDetails/FMSDetailsView';
import FMSIntegrationsView from '../../Views/Settings/FMSIntegrations/FMSIntegrationsView';
import UserJourneysView from '../../Views/Workflows/UserJourneysView';
import { NavBarLayout } from '../Layout/NavBar/NavBarView';

import ErrorView from '../../Components/ErrorView/ErrorView';
import { RouterWorksheetData } from '../../Components/Worksheets/Types/Worksheet.Interface';
import { AnalyticsPlaygroundView } from '../../Views/Admin/Analytics/AnalyticsPlayground.View';
import { CRMView } from '../../Views/Analytics/CRM/CRM.View';
import BrainsView from '../../Views/Brains/BrainsView';
import TranscriptView from '../../Views/Conversations/Transcript/TranscriptView';
import NotificationsView from '../../Views/Notifications/Notifications.View';
import { SpotView } from '../../Views/Properties/Spot/Spot.View';
import EscalationHistoryView from '../../Views/Settings/EscalationHistory/EscalationHistoryView';
import { SequenceDetailsView } from '../../Views/Settings/Sequences/Details/SequenceDetails.View';
import { SequenceRecordDetails } from '../../Views/Settings/Sequences/Record/SequenceRecordDetails';
import { SequenceSettingsView } from '../../Views/Settings/Sequences/SequencesSettings.View';
import MainLayout from '../Layout/MainLayout';
import { RouterRedirect } from './Helpers/RouterRedirect';


// const UserJourneysView = lazy(() => import("../../Views/Workflows/UserJourneysView"));
// const EditorView = lazy(() => import("../../Views/Quests/EditorWrapper"));
// const BrainView = lazy(() => import("../../Views/Brains/BrainsView"));
// const AppsSettings          = lazy(() => import('../../Views/Settings/Apps/AppsView'));
// const BrainView             = lazy(() => import('../../Views/Brains/BrainsView'));
// const AnalyticsSettingsView = lazy(() => import('../../Views/Settings/Analytics/Analytics.Settings.View'));
// const FMSIntegrationsView   = lazy(() => import('../../Views/Settings/FMSIntegrations/FMSIntegrationsView'));
// const FMSDetailsView        = lazy(() => import('../../Views/Settings/FMSIntegrations/FMSDetails/FMSDetailsView'));
// const EmbedTester           = lazy(() => import('../../Views/Admin/EmbedTester/EmbedTester'));
// const AgentSettingsView     = lazy(() => import('../../Views/Conversations/Components/Agent/AgentSettingsView'));
// const EscalationsHistory    = lazy(() => import('../../Views/Settings/EscalationHistory/EscalationHistoryView'));
// const ReviewsView           = lazy(() => import('../../Views/Reviews/Reviews.View'));

function WrapInScope(element: any, scopes?: APIScope) {
  if (scopes) {
    return <RouteShowIf scopes={[scopes]}>{element}</RouteShowIf>;
  } else {
    return element;
  }
}

function RouteWorksheetForData(data: RouterWorksheetData[]) {
  return data.map((item, index) => {
    const to = item.to;

    if (item.children && item.children.length > 0) {
      return (
        <Route key={'parent' + to} path={to} element={<Outlet />}> {RouteWorksheetForData(item.children)}
        </Route>
      );
    } // We need to return a react fragment to avoid a warning
    return (
      <React.Fragment key={to + 'parent'}>
        <Route key={to + 'default'} path={to} element={<Suspense fallback={<PageLoadingView />}>{WrapInScope(item.element, item.apiScope)}</Suspense>} />
        <Route key={to + 'action'} path={to + '/:action'} element={<Suspense fallback={<PageLoadingView />}>{WrapInScope(item.element, item.apiScope)}</Suspense>} />
      </React.Fragment>
    );
  });
}

export default function RouterLoggedIn() {
  const navigate = useNavigate();
  useEffect(() => {
    NavigationHelper.navFunction = navigate as any;
  }, [navigate]);


  return (
    <>
      <Routes>
        <Route path="/select-org" element={<Navigate to={'/'} />} />

        <Route element={<MainLayout />}>
          {/* admin routes */}
          <Route path="/admin" element={<RouteShowIf scopes={[APIScope.SUPER_ADMIN_ONLY]}><AdminView /></RouteShowIf>}>
            <Route path="embed" element={<Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}><EmbedTester /></Suspense>} />
            <Route path="embed/:orgId" element={<Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}>       <EmbedTester />     </Suspense>} />
            <Route path="embed/:orgId/:embedId" element={<Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}>       <EmbedTester />     </Suspense>} />
            <Route path="variables" element={<AdminVariablesEditor />} />
            <Route path="server-debug" element={<ServerDebugView />} />
            <Route path="onboarding" element={<OnboardingView />} />
            <Route path="analytics" element={<Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}>       <AnalyticsPlaygroundView />     </Suspense>} />
            <Route path="*" element={<Navigate to="/admin/server-debug" />} />
          </Route>

          {/* settings routes */}
          <Route path="agent/settings" element={<Suspense fallback={<PageLoadingView />}><AgentSettingsView /></Suspense>} />
          <Route path="/settings" element={<SettingsView />}>
            <Route path="ai-assist" element={<RouteShowIf scopes={[APIScope.WORKFLOWS_EDIT]}>       <Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}>         <AIAssistSettingsView />       </Suspense>     </RouteShowIf>} />

            <Route path="apps" element={<RouteShowIf scopes={[APIScope.WORKFLOWS_EDIT]}>       <Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}>         <AppSettingsView />       </Suspense>     </RouteShowIf>} />
            <Route path="apps/:appType" element={<RouteShowIf scopes={[APIScope.WORKFLOWS_EDIT]}>       <Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}>         <AppSettingsView />       </Suspense>     </RouteShowIf>} />
            <Route path="apps/:appType/:appId" element={<RouteShowIf scopes={[APIScope.WORKFLOWS_EDIT]}>       <Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}>         <AppSettingsView />       </Suspense>     </RouteShowIf>} />
            <Route path="brains" element={<RouteShowIf scopes={[APIScope.WORKFLOWS_EDIT]}>       <BrainsSettingView />     </RouteShowIf>} />
            <Route path="roles" element={<RouteShowIf scopes={[APIScope.ROLES_VIEW]}>       <RolesSettingsView />     </RouteShowIf>} />
            <Route path="roles/:roleId" element={<RouteShowIf scopes={[APIScope.ROLES_VIEW]}>       <RolesSettingsView />     </RouteShowIf>} />
            <Route path="members" element={<RouteShowIf scopes={[APIScope.MEMBERS_GET]}>       <MembersSettingsView />     </RouteShowIf>} />
            <Route path="members/:memberOrInvite/:objectId" element={<RouteShowIf scopes={[APIScope.MEMBERS_GET]}>       <MembersSettingsView />     </RouteShowIf>} />
            <Route path="account" element={<AccountSettingsView />} /> <Route path="*" element={<Navigate to="/account" />} />
            <Route path="escalations" element={<RouteShowIf scopes={[APIScope.ESCALATIONS_EDIT]}>              <EscalationsView />     </RouteShowIf>} />
            <Route path="escalation-history" element={<RouteShowIf scopes={[APIScope.ESCALATIONS_EDIT]}>       <Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}>         <EscalationHistoryView />       </Suspense>      </RouteShowIf>} >
              <Route path=":escalationId" element={<RouteShowIf scopes={[APIScope.ESCALATIONS_EDIT]}>       <Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}>         <EscalationHistoryView />       </Suspense>      </RouteShowIf>} />
            </Route>
            <Route path="analytics" element={<Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}>       <AnalyticsSettingsView />     </Suspense>} />
            <Route path="fms-integrations" element={<RouteShowIf scopes={[APIScope.FMS_VIEW]}>       <Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}>         <FMSIntegrationsView />       </Suspense>     </RouteShowIf>} />
            <Route path="sequences" element={<RouteShowIf scopes={[APIScope.SEQUENCES_VIEW]}>       <Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}>         <SequenceSettingsView />       </Suspense>      </RouteShowIf>} >
              <Route path=":sequenceId" element={<RouteShowIf scopes={[APIScope.SEQUENCES_VIEW]}>       <Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}>         <SequenceDetailsView />       </Suspense>      </RouteShowIf>} />
            </Route>
          </Route>

          {/* properties routes */}
          <Route path="properties" element={<RouteShowIf scopes={[APIScope.LOCATIONS_ALL_VIEW]}><PropertiesView /></RouteShowIf>}>
            {/* LOCATION ROUTES */}
            <Route path="group/:groupId/location/:locationId/page/details" element={<WorksheetLayoutView showHeader={true} data={LocationWorksheetsData[LocationScope.LOCATION]} page={'details'} />}>   {RouteWorksheetForData(LocationWorksheetsData[LocationScope.LOCATION])}   <Route path="*" element={<Navigate to="info" />} /> </Route>
            {/* GROUP ROUTES */}
            <Route path="group/:groupId/page/details" element={<WorksheetLayoutView showHeader={true} data={LocationWorksheetsData[LocationScope.GROUP]} page={'details'} />}>   {RouteWorksheetForData(LocationWorksheetsData[LocationScope.GROUP])}   <Route path="*" element={<Navigate to="info" />} /> </Route>
            {/* GROUP ORG */}
            <Route path="page/details" element={<WorksheetLayoutView showHeader={true} data={LocationWorksheetsData[LocationScope.ORG]} page={'details'} />}>   {RouteWorksheetForData(LocationWorksheetsData[LocationScope.ORG])}   <Route path="*" element={<Navigate to="info" />} /> </Route>
            {/* LOCATION CORTEX ROUTES */}
            <Route path="group/:groupId/location/:locationId/page/cortex" element={<WorksheetLayoutView data={CortexWorksheetsData[LocationScope.LOCATION]} page={'cortex'} />}>   {RouteWorksheetForData(CortexWorksheetsData[LocationScope.LOCATION])}   <Route path="*" element={<Navigate to="/" />} /> </Route>
            {/* GROUP CORTEX ROUTES */}
            <Route path="group/:groupId/page/cortex" element={<WorksheetLayoutView data={CortexWorksheetsData[LocationScope.GROUP]} page={'cortex'} />}>   {RouteWorksheetForData(CortexWorksheetsData[LocationScope.GROUP])}   <Route path="*" element={<Navigate to="/" />} /> </Route>
            {/* ORG CORTEX ROUTES */}
            <Route path="page/cortex" element={<WorksheetLayoutView data={CortexWorksheetsData[LocationScope.ORG]} page={'cortex'} />}>   {RouteWorksheetForData(CortexWorksheetsData[LocationScope.ORG])}   <Route path="*" element={<Navigate to="/" />} /> </Route>
            {/* GROUP CORTEX ORG */}

            <Route path="group/:groupId/location/:locationId/page/spot" element={<SpotView />} />
            {/* GROUP CORTEX ROUTES */}
            <Route path="group/:groupId/page/spot" element={<SpotView />} />
            {/* ORG CORTEX ROUTES */}
            <Route path="page/spot" element={<SpotView />} />

            <Route path="group/:groupId/page/*" element={<Navigate to="./details" />} />
            <Route path="group/:groupId/location/:locationId/page/*" element={<Navigate to="./details" />} />

            <Route path="page/brands" element={<BrandsView />}>
              <Route path=":brandId" element={<BrandDetailsView />} />
            </Route>
            <Route path="page/chatbots" element={<EmbedsView />}>
              <Route path=":embedId" element={<EmbedDetailsView />} />
            </Route>
            <Route path="/properties" element={<Navigate to="/properties/page/details/info" />} />
          </Route>
        </Route>

        {/* invite routes */}
        <Route path="/invite" element={<LoginLayout />}>
          <Route path="/invite/:inviteUUID" element={<InviteView />} />
        </Route>

        {/* dashboard routes with navbar */}
        <Route path="/" element={<NavBarLayout />}> <Route path="brain/:brainSlug" element={<RouteShowIf scopes={[APIScope.WORKFLOWS_EDIT]}>       <Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}>         <BrainsView />       </Suspense>     </RouteShowIf>} />
          <Route path="/notifications" element={<NotificationsView />} />
          <Route path="/workflows" element={<RouteShowIf scopes={[APIScope.WORKFLOWS_VIEW]}>       <UserJourneysView />     </RouteShowIf>} /> <Route path="quest/:questSlug" element={<RouteShowIf scopes={[APIScope.WORKFLOWS_VIEW]}>       <EditorWrapper />     </RouteShowIf>} /> <Route path="matrix/:questSlug" element={<RouteShowIf scopes={[APIScope.WORKFLOWS_VIEW]}>              <Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}>         <EditorWrapper />       </Suspense>     </RouteShowIf>} /> {/* <Route path="brain/:brainSlug" element={<RouteShowIf scopes={[APIScope.WORKFLOWS_VIEW]}>  <Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}><BrainView /></Suspense></RouteShowIf>} /> */}
          <Route path="fms/:id" element={<RouteShowIf scopes={[APIScope.FMS_EDIT]}>       <Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}>         <FMSDetailsView />       </Suspense>     </RouteShowIf>} />

          <Route path="conversations" element={<ConversationsView />} />
          <Route path="conversations/all/:userId" element={<RouterRedirect to={"/conversations/user/:userId"} />} />

          <Route path="conversations/user/:userId" element={<ConversationsView />} />

          <Route path="reviews" element={<RouteShowIf scopes={[APIScope.REVIEWS_EDIT]}>       <Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}>         <ReviewsView />       </Suspense>     </RouteShowIf>} />
          <Route path="/" element={<DashboardView />} />

          <Route path="analytics/crm" element={<CRMView />} />
          <Route path="analytics/crm/:startDate/:endDate" element={<CRMView />} />

          <Route path="transcript/:userId" element={<TranscriptView />} />
          <Route path="/settings/sequences/:sequenceId/record/:recordId" element={<RouteShowIf scopes={[APIScope.SEQUENCES_VIEW]}>       <Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}>         <SequenceRecordDetails />       </Suspense>      </RouteShowIf>} />
        </Route>

        <Route path="/*" element={<ErrorView />} />
      </Routes>
    </>
  );
}
